// frontend/src/hooks/useETADisplayData.js
// Custom hook for calculating ETA display data based on trip status

import { useMemo } from 'react';
import { getTripDisplayStops } from '../utils/tripDisplayStops';
import { buildCompletedComparisonDisplayRows } from '../utils/completedComparisonDisplayRows';
import { parseBackendUtcInstant } from '../utils/dateTimeFormatter';
import { buildEntityScheduledDepartureDate } from '../utils/entityTimeZone';

const ETA_DISABLED_REASON = 'eta_disabled_by_entity_preference';

const getEstimatedWaypoints = (estimated) => (
    Array.isArray(estimated) ? estimated : (estimated?.waypoints || [])
);

const isDistanceOnlyEtaPayload = (payload = null) => (
    payload?.provider === 'none'
    || payload?.eta_disabled_reason === ETA_DISABLED_REASON
);

const sanitizeDistanceOnlyDisplayWaypoint = (waypoint = {}) => ({
    ...waypoint,
    eta_disabled_reason: waypoint.eta_disabled_reason || ETA_DISABLED_REASON,
    eta_minutes_from_now: null,
    estimated_arrival_time: null,
    dynamic_arrival_time: null,
    predicted_arrival_time: null,
    eta_minutes_with_operational_delay: null,
    boundary_time_with_operational_delay: null,
    cumulative_arrival_delay_minutes: 0,
    cumulative_departure_delay_minutes: 0,
    trip_duration_minutes: 0,
    total_duration_minutes: 0,
    cumulative_duration_from_start_seconds: null,
    has_eta_data: false
});

const hasWaypointActivity = (waypoint = {}) => (
    Number(waypoint.pickup_count) > 0
    || Number(waypoint.dropoff_count) > 0
    || Number(waypoint.pickup_riders) > 0
    || Number(waypoint.dropoff_riders) > 0
);

const hasWaypointArrival = (waypoint = {}) => Boolean(waypoint.waypoint_arrival_time ?? waypoint.arrival_time);

const hasWaypointDeparture = (waypoint = {}) => Boolean(waypoint.waypoint_departure_time ?? waypoint.departure_time);

import { parseNullableNonNegativeMetric } from '../utils/nullableMetric';

export const getDisplayStayDurationMinutes = (waypoint = {}, stopData = {}, preferPredicted = false) => {
    const candidateFields = preferPredicted
        ? [
            waypoint.predicted_stay_duration_minutes,
            waypoint.stay_duration_minutes,
            stopData.stay_duration_minutes
        ]
        : [
            waypoint.stay_duration_minutes,
            stopData.stay_duration_minutes,
            waypoint.predicted_stay_duration_minutes
        ];

    for (const value of candidateFields) {
        if (value === null || value === undefined || value === '') {
            continue;
        }

        const numericValue = parseFloat(value);
        if (Number.isFinite(numericValue)) {
            return numericValue;
        }
    }

    return 0;
};

export const deriveDisplayPassedState = (stopData = {}, waypoint = {}) => {
    if (stopData.is_manual_skip) {
        return true;
    }
    if (hasWaypointDeparture(stopData) || hasWaypointDeparture(waypoint)) {
        return true;
    }
    if (hasWaypointArrival(stopData) || hasWaypointArrival(waypoint)) {
        return false;
    }
    if (stopData.is_passed !== undefined && stopData.is_passed !== null) {
        return Boolean(stopData.is_passed);
    }
    if (waypoint.is_passed !== undefined && waypoint.is_passed !== null) {
        return Boolean(waypoint.is_passed);
    }
    return false;
};

export const deriveDisplaySkippedState = (stopData = {}, waypoint = {}) => {
    if (stopData.is_manual_skip) {
        return true;
    }
    if (stopData.is_skipped !== undefined && stopData.is_skipped !== null) {
        return Boolean(stopData.is_skipped);
    }
    if (waypoint.is_skipped !== undefined && waypoint.is_skipped !== null) {
        return Boolean(waypoint.is_skipped);
    }
    return false;
};

export const buildPlanEffectiveSequenceMap = (tripPlan) => {
    const sequenceMap = new Map();
    const orderedWaypointIds = [];

    for (const segment of tripPlan?.segments || []) {
        if (segment?.from_waypoint_id != null && !orderedWaypointIds.includes(segment.from_waypoint_id)) {
            orderedWaypointIds.push(segment.from_waypoint_id);
        }
        if (segment?.to_waypoint_id != null && !orderedWaypointIds.includes(segment.to_waypoint_id)) {
            orderedWaypointIds.push(segment.to_waypoint_id);
        }
    }

    orderedWaypointIds.forEach((waypointId, index) => {
        sequenceMap.set(Number(waypointId), index);
    });

    return sequenceMap;
};

export const buildDisplaySequenceMap = (tripStops = [], tripPlan = null) => {
    const planSequenceMap = buildPlanEffectiveSequenceMap(tripPlan);
    const getStopSortValue = (stop) => {
        if (stop?.effective_sequence !== null && stop?.effective_sequence !== undefined) {
            const effectiveSequence = Number(stop.effective_sequence);
            if (Number.isFinite(effectiveSequence)) {
                return effectiveSequence;
            }
        }

        const sequenceNumber = Number(stop?.sequence_number);
        return Number.isFinite(sequenceNumber) ? sequenceNumber : Number.MAX_SAFE_INTEGER;
    };
    const orderedStops = [...(tripStops || [])]
        .filter((stop) => Number.isFinite(Number(stop?.waypoint_id)))
        .sort((left, right) => getStopSortValue(left) - getStopSortValue(right));

    const displaySequenceMap = new Map(planSequenceMap);

    let index = 0;
    while (index < orderedStops.length) {
        const waypointId = Number(orderedStops[index]?.waypoint_id);
        if (displaySequenceMap.has(waypointId)) {
            index += 1;
            continue;
        }

        const missingCluster = [];
        while (index < orderedStops.length) {
            const stop = orderedStops[index];
            const missingWaypointId = Number(stop?.waypoint_id);
            if (displaySequenceMap.has(missingWaypointId)) {
                break;
            }
            missingCluster.push(missingWaypointId);
            index += 1;
        }

        const previousStop = orderedStops
            .slice(0, index - missingCluster.length)
            .reverse()
            .find((stop) => displaySequenceMap.has(Number(stop?.waypoint_id)));
        const nextStop = orderedStops
            .slice(index)
            .find((stop) => displaySequenceMap.has(Number(stop?.waypoint_id)));

        const previousSequence = previousStop
            ? displaySequenceMap.get(Number(previousStop.waypoint_id))
            : null;
        const nextSequence = nextStop
            ? displaySequenceMap.get(Number(nextStop.waypoint_id))
            : null;

        if (Number.isFinite(previousSequence) && Number.isFinite(nextSequence)) {
            const step = (nextSequence - previousSequence) / (missingCluster.length + 1);
            missingCluster.forEach((missingWaypointId, missingIndex) => {
                displaySequenceMap.set(
                    missingWaypointId,
                    previousSequence + (step * (missingIndex + 1))
                );
            });
            continue;
        }

        if (Number.isFinite(previousSequence)) {
            missingCluster.forEach((missingWaypointId, missingIndex) => {
                displaySequenceMap.set(missingWaypointId, previousSequence + missingIndex + 1);
            });
            continue;
        }

        if (Number.isFinite(nextSequence)) {
            missingCluster
                .slice()
                .reverse()
                .forEach((missingWaypointId, missingIndex) => {
                    displaySequenceMap.set(missingWaypointId, nextSequence - missingIndex - 1);
                });
            continue;
        }

        missingCluster.forEach((missingWaypointId, missingIndex) => {
            displaySequenceMap.set(missingWaypointId, missingIndex);
        });
    }

    return displaySequenceMap;
};

const getWaypointSortValue = (waypoint, displaySequenceMap) => {
    const waypointId = Number(waypoint?.waypoint_id);
    if (displaySequenceMap?.has(waypointId)) {
        return displaySequenceMap.get(waypointId);
    }
    if (waypoint?.effective_sequence !== undefined && waypoint?.effective_sequence !== null) {
        return waypoint.effective_sequence;
    }
    return waypoint?.sequence_number ?? Number.MAX_SAFE_INTEGER;
};

/**
 * Sort waypoints by effective sequence when available, otherwise by sequence number.
 * Filters out skipped waypoints for reorderable trips.
 * See docs/ETA_TRIP_TRACKING_REORDERABLE_WAYPOINTS.md for details.
 */
const sortWaypointsByEffectiveOrder = (waypoints, displaySequenceMap = null) => {
    return [...waypoints].sort((a, b) => {
        const seqA = getWaypointSortValue(a, displaySequenceMap);
        const seqB = getWaypointSortValue(b, displaySequenceMap);
        if (seqA !== seqB) {
            return seqA - seqB;
        }
        return (a.sequence_number ?? 0) - (b.sequence_number ?? 0);
    });
};

const isStartLikeWaypoint = (waypoint = {}) => (
    Boolean(waypoint.is_departure)
    || waypoint.point_type === 'start'
    || waypoint.stop_position === 'start'
    || Number(waypoint.sequence_number) === 0
    || Number(waypoint.effective_sequence) === 0
);

export const getCompletedTripStartReferenceWaypoint = (waypoints = []) => (
    sortWaypointsByEffectiveOrder(waypoints).find((waypoint) => !isStartLikeWaypoint(waypoint)) || null
);

export const getCompletedTripStartApiEstimate = (waypoints = []) => {
    const firstWaypoint = getCompletedTripStartReferenceWaypoint(waypoints);
    if (!firstWaypoint?.predicted_arrival_time) {
        return null;
    }

    const tripDurationMinutes = Number(firstWaypoint.predicted_trip_duration_minutes);
    if (!Number.isFinite(tripDurationMinutes)) {
        return null;
    }

    const firstWaypointTime = parseBackendUtcInstant(firstWaypoint.predicted_arrival_time);
    if (!firstWaypointTime || Number.isNaN(firstWaypointTime.getTime())) {
        return null;
    }

    return {
        firstWaypoint,
        apiEstimatedArrivalTime: new Date(
            firstWaypointTime.getTime() - (tripDurationMinutes * 60 * 1000)
        ).toISOString()
    };
};

export const calculateInstantVarianceMinutes = (actual, estimated) => {
    const actualDate = parseBackendUtcInstant(actual);
    const estimatedDate = parseBackendUtcInstant(estimated);
    if (
        !actualDate
        || !estimatedDate
        || Number.isNaN(actualDate.getTime())
        || Number.isNaN(estimatedDate.getTime())
    ) {
        return null;
    }
    return Math.round((actualDate.getTime() - estimatedDate.getTime()) / 60000);
};

const shouldRestoreMissingStop = (stopData, bypassedWaypointIds, passageData = stopData) => {
    const waypointId = Number(stopData?.waypoint_id);
    const isHistorical = deriveDisplayPassedState(passageData) || deriveDisplaySkippedState(passageData);
    if (bypassedWaypointIds.has(waypointId) && !isHistorical) {
        return false;
    }
    if (stopData?.point_type === 'stop') {
        return true;
    }
    return isHistorical || hasWaypointActivity(stopData);
};

// With ETA unavailable, stop corrections can explicitly clear retained passage.
// Resolve those fields once: null clears, while omission allows fallback. This
// rule is opt-in below: ordinary trip nulls cannot erase a newer accepted ETA.
const resolveDisplayPassage = (liveStop, tripStop, waypoint = {}) => {
    const sources = [liveStop, tripStop, waypoint];
    const timestamp = field => {
        const source = sources.find(row => Object.prototype.hasOwnProperty.call(row, field)
            && row[field] !== undefined);
        return source ? source[field] : null;
    };
    return {
        is_manual_skip: liveStop.is_manual_skip ?? tripStop.is_manual_skip ?? waypoint.is_manual_skip,
        is_skipped: liveStop.is_skipped ?? tripStop.is_skipped ?? waypoint.is_skipped,
        is_passed: liveStop.is_passed ?? tripStop.is_passed ?? waypoint.is_passed,
        waypoint_arrival_time: timestamp('waypoint_arrival_time'),
        waypoint_departure_time: timestamp('waypoint_departure_time')
    };
};

export const buildInProgressWebsocketWaypoints = ({
    waypoints,
    tripStops,
    liveStops,
    tripPlan,
    pickupStopId,
    dropoffStopId,
    retiredWaypointIds = [],
    preserveExplicitPassageClears = false
}) => {
    const stopById = new Map((tripStops || []).map(stop => [Number(stop.waypoint_id), stop]));
    const liveStopById = new Map((liveStops || []).map(stop => [Number(stop.waypoint_id), stop]));
    const bypassedWaypointIds = new Set((tripPlan?.bypassed_waypoint_ids || []).map(Number));
    const displaySequenceMap = buildDisplaySequenceMap(tripStops, tripPlan);
    const mergedById = new Map();
    const retiredIds = new Set(retiredWaypointIds.map(Number));

    for (const waypoint of waypoints || []) {
        const waypointId = Number(waypoint?.waypoint_id);
        if (!Number.isFinite(waypointId)) {
            continue;
        }

        const stopData = stopById.get(waypointId) || {};
        const liveStopData = liveStopById.get(waypointId) || {};
        const preferredPassageStopData = preserveExplicitPassageClears
            ? resolveDisplayPassage(liveStopData, stopData, waypoint)
            : {
                is_manual_skip: liveStopData.is_manual_skip ?? stopData.is_manual_skip,
                is_skipped: liveStopData.is_skipped ?? stopData.is_skipped,
                is_passed: liveStopData.is_passed ?? stopData.is_passed,
                waypoint_arrival_time: liveStopData.waypoint_arrival_time ?? stopData.waypoint_arrival_time,
                waypoint_departure_time: liveStopData.waypoint_departure_time ?? stopData.waypoint_departure_time
            };
        if (preserveExplicitPassageClears && bypassedWaypointIds.has(waypointId)
            && !shouldRestoreMissingStop({ ...waypoint, ...stopData }, bypassedWaypointIds, preferredPassageStopData)) {
            continue;
        }
        const hasActiveOperationalDelayAuthority = waypoint.eta_minutes_with_operational_delay !== null
            && waypoint.eta_minutes_with_operational_delay !== undefined
            && waypoint.eta_minutes_with_operational_delay !== ''
            && Number.isFinite(Number(waypoint.eta_minutes_with_operational_delay));
        const activeOperationalBoundary = hasActiveOperationalDelayAuthority
            ? (waypoint.boundary_time_with_operational_delay ?? null)
            : null;
        // Provider arrival metadata survives an unproven live metric origin for
        // later recalculation. Explicit backend unavailability must not turn that
        // immutable timestamp into an apparently current arrival clock.
        const liveTimeUnavailable = (waypoint.eta_calculation_source === 'unavailable'
            || retiredIds.has(waypointId))
            && waypoint.eta_minutes_from_now == null;
        mergedById.set(waypointId, {
            waypoint_id: waypointId,
            waypoint_name: waypoint.waypoint_name || stopData.waypoint_name,
            sequence_number: waypoint.sequence_number ?? stopData.sequence_number ?? 0,
            effective_sequence: waypoint.effective_sequence ?? displaySequenceMap.get(waypointId),
            point_type: waypoint.point_type || stopData.point_type || 'stop',
            estimated_arrival_time: liveTimeUnavailable ? null : activeOperationalBoundary
                || waypoint.estimated_arrival_time
                || waypoint.dynamic_arrival_time
                || waypoint.predicted_arrival_time
                || null,
            total_distance_meters: parseNullableNonNegativeMetric(waypoint.distance_from_previous_waypoint),
            trip_duration_minutes: parseNullableNonNegativeMetric(waypoint.predicted_trip_duration_minutes),
            // PATH 2 can reactivate a stop before predicted_stay_duration_minutes is
            // repopulated, so fall back to trip-stop stay data for websocket rows.
            stay_duration_minutes: getDisplayStayDurationMinutes(waypoint, stopData, true),
            can_pickup: stopData.can_pickup || false,
            can_dropoff: stopData.can_dropoff || false,
            pickup_riders: stopData.pickup_riders || 0,
            dropoff_riders: stopData.dropoff_riders || 0,
            pickup_luggages: stopData.pickup_luggages || 0,
            dropoff_luggages: stopData.dropoff_luggages || 0,
            is_user_pickup: stopData.is_user_pickup || waypointId === pickupStopId,
            is_user_dropoff: stopData.is_user_dropoff || waypointId === dropoffStopId,
            address: stopData.address,
            latitude: stopData.latitude,
            longitude: stopData.longitude,
            is_departure: waypoint.sequence_number === 0
                || stopData.sequence_number === 0
                || waypoint.point_type === 'start'
                || stopData.point_type === 'start',
            boarding_location_info: stopData.boarding_location_info || waypoint.boarding_location_info || null,
            boarding_location_url: stopData.boarding_location_url || waypoint.boarding_location_url || null,
            boarding_location_image: stopData.boarding_location_image || waypoint.boarding_location_image || null,
            distance_meters_from_current: waypoint.distance_meters_from_current,
            eta_minutes_from_now: waypoint.eta_minutes_with_operational_delay
                ?? waypoint.eta_minutes_from_now,
            raw_eta_minutes_from_now: waypoint.eta_minutes_from_now,
            eta_minutes_with_operational_delay: waypoint.eta_minutes_with_operational_delay ?? null,
            boundary_time_with_operational_delay: activeOperationalBoundary,
            cumulative_arrival_delay_minutes: waypoint.cumulative_arrival_delay_minutes ?? 0,
            cumulative_departure_delay_minutes: waypoint.cumulative_departure_delay_minutes ?? 0,
            eta_disabled_reason: waypoint.eta_disabled_reason ?? null,
            // Immutable provider metadata remains on the backend payload; a
            // retired live row cannot use any of its clocks as a display fallback.
            dynamic_arrival_time: liveTimeUnavailable ? null : waypoint.dynamic_arrival_time ?? null,
            predicted_arrival_time: liveTimeUnavailable ? null : waypoint.predicted_arrival_time ?? null,
            distance_source: waypoint.distance_source ?? null,
            segment_distance_source: parseNullableNonNegativeMetric(waypoint.distance_meters_from_current) === null
                ? null : waypoint.segment_distance_source ?? null,
            distance_authority: waypoint.distance_authority ?? null,
            eta_calculation_source: waypoint.eta_calculation_source ?? null,
            is_passed: deriveDisplayPassedState(preferredPassageStopData, preserveExplicitPassageClears ? undefined : waypoint),
            is_skipped: deriveDisplaySkippedState(preferredPassageStopData, preserveExplicitPassageClears ? undefined : waypoint),
            is_manual_skip: Boolean(preferredPassageStopData.is_manual_skip),
            cumulative_distance_from_start_meters: stopData.cumulative_distance_from_start_meters ?? null,
            cumulative_duration_from_start_seconds: stopData.cumulative_duration_from_start_seconds ?? null,
            waypoint_arrival_time: preferredPassageStopData.waypoint_arrival_time
                ?? (preserveExplicitPassageClears ? null : waypoint.waypoint_arrival_time) ?? null,
            waypoint_departure_time: preferredPassageStopData.waypoint_departure_time
                ?? (preserveExplicitPassageClears ? null : waypoint.waypoint_departure_time) ?? null,
        });
    }

    for (const stopData of tripStops || []) {
        const waypointId = Number(stopData.waypoint_id);
        if (mergedById.has(waypointId)
            || (!preserveExplicitPassageClears && !shouldRestoreMissingStop(stopData, bypassedWaypointIds))) {
            continue;
        }

        const liveStopData = liveStopById.get(waypointId) || {};
        const preferredPassageStopData = preserveExplicitPassageClears
            ? resolveDisplayPassage(liveStopData, stopData)
            : {
                ...stopData,
                is_manual_skip: liveStopData.is_manual_skip ?? stopData.is_manual_skip,
                is_skipped: liveStopData.is_skipped ?? stopData.is_skipped,
                is_passed: liveStopData.is_passed ?? stopData.is_passed,
                waypoint_arrival_time: liveStopData.waypoint_arrival_time ?? stopData.waypoint_arrival_time,
                waypoint_departure_time: liveStopData.waypoint_departure_time ?? stopData.waypoint_departure_time
            };
        if (preserveExplicitPassageClears && !shouldRestoreMissingStop(stopData, bypassedWaypointIds, preferredPassageStopData)) {
            continue;
        }
        mergedById.set(waypointId, {
            waypoint_id: waypointId,
            waypoint_name: stopData.waypoint_name,
            sequence_number: stopData.sequence_number ?? 0,
            effective_sequence: displaySequenceMap.get(waypointId),
            point_type: stopData.point_type || 'stop',
            estimated_arrival_time: null,
            total_distance_meters: null,
            trip_duration_minutes: null,
            stay_duration_minutes: 0,
            can_pickup: stopData.can_pickup || false,
            can_dropoff: stopData.can_dropoff || false,
            pickup_riders: stopData.pickup_riders || 0,
            dropoff_riders: stopData.dropoff_riders || 0,
            pickup_luggages: stopData.pickup_luggages || 0,
            dropoff_luggages: stopData.dropoff_luggages || 0,
            is_user_pickup: stopData.is_user_pickup || waypointId === pickupStopId,
            is_user_dropoff: stopData.is_user_dropoff || waypointId === dropoffStopId,
            address: stopData.address,
            latitude: stopData.latitude,
            longitude: stopData.longitude,
            is_departure: stopData.sequence_number === 0 || stopData.point_type === 'start',
            boarding_location_info: stopData.boarding_location_info || null,
            boarding_location_url: stopData.boarding_location_url || null,
            boarding_location_image: stopData.boarding_location_image || null,
            distance_meters_from_current: null,
            eta_minutes_from_now: null,
            distance_source: null,
            segment_distance_source: null,
            distance_authority: null,
            eta_calculation_source: null,
            is_passed: deriveDisplayPassedState(preferredPassageStopData),
            is_skipped: deriveDisplaySkippedState(preferredPassageStopData),
            is_manual_skip: Boolean(preferredPassageStopData.is_manual_skip),
            cumulative_distance_from_start_meters: stopData.cumulative_distance_from_start_meters ?? null,
            cumulative_duration_from_start_seconds: stopData.cumulative_duration_from_start_seconds ?? null,
            waypoint_arrival_time: preferredPassageStopData.waypoint_arrival_time ?? null,
            waypoint_departure_time: preferredPassageStopData.waypoint_departure_time ?? null,
        });
    }

    return sortWaypointsByEffectiveOrder(Array.from(mergedById.values()), displaySequenceMap);
};

export const buildNonRealtimeDisplayWaypoints = ({
    waypoints,
    tripStops,
    tripPlan,
    pickupStopId,
    dropoffStopId,
    isPreTrip,
    canUseStaticOperationalDelay = isPreTrip,
    baseDateTime,
    isWebsocketData = false
}) => {
    const hasValidBaseDateTime = baseDateTime instanceof Date
        && !Number.isNaN(baseDateTime.getTime());
    const displaySequenceMap = buildDisplaySequenceMap(tripStops, tripPlan);
    const bypassedWaypointIds = new Set((tripPlan?.bypassed_waypoint_ids || []).map(Number));
    const stopById = new Map((tripStops || []).map((stop) => [Number(stop?.waypoint_id), stop]));
    const sortedWaypoints = sortWaypointsByEffectiveOrder(waypoints, displaySequenceMap);
    const visibleWaypoints = sortedWaypoints.filter((wp) => {
        const waypointId = Number(wp?.waypoint_id);
        if (!Number.isFinite(waypointId)) {
            return false;
        }

        const stopData = stopById.get(waypointId) || {};
        const isHistorical = deriveDisplayPassedState(stopData, wp) || deriveDisplaySkippedState(stopData, wp);
        if (bypassedWaypointIds.has(waypointId) && !isHistorical) {
            return false;
        }

        return true;
    });
    const mergedById = new Map();
    let cumulativeMinutes = 0;
    let cumulativeStaySeconds = 0;

    for (const wp of visibleWaypoints) {
        const waypointId = Number(wp?.waypoint_id);
        if (!Number.isFinite(waypointId)) {
            continue;
        }

        const stopData = stopById.get(waypointId) || {};
        const tripDurationField = isWebsocketData ? 'predicted_trip_duration_minutes' : 'total_duration_minutes';
        const distanceField = isWebsocketData ? 'distance_from_previous_waypoint' : 'total_distance_meters';
        const sequenceNumber = wp.sequence_number ?? stopData.sequence_number ?? 0;
        const effectiveSequence = wp.effective_sequence ?? displaySequenceMap.get(waypointId);
        const pointType = wp.point_type || stopData.point_type || 'stop';
        const isStartRow = isStartLikeWaypoint({
            ...stopData,
            ...wp,
            sequence_number: sequenceNumber,
            effective_sequence: effectiveSequence,
            point_type: pointType
        });
        const isSkipped = deriveDisplaySkippedState(stopData, wp);
        const tripDuration = parseNullableNonNegativeMetric(wp[tripDurationField]);
        let stayMinutes = 0;
        const stayDuration = isWebsocketData
            ? getDisplayStayDurationMinutes(wp, stopData, true)
            : getDisplayStayDurationMinutes(wp, stopData, false);
        const hasActivity = hasWaypointActivity(stopData) || hasWaypointActivity(wp);

        let estimatedArrivalTime = null;
        let hasEtaData = false;
        const backendCumulativeSeconds = stopData.cumulative_duration_from_start_seconds
            ?? wp.cumulative_duration_from_start_seconds;
        const usesStaticScheduleClock = isPreTrip || canUseStaticOperationalDelay;

        if (usesStaticScheduleClock) {
            if (isSkipped) {
                estimatedArrivalTime = null;
            } else if (isStartRow && hasValidBaseDateTime) {
                estimatedArrivalTime = baseDateTime.toISOString();
                hasEtaData = true;
            } else if (
                hasValidBaseDateTime
                && backendCumulativeSeconds != null
                && Number(backendCumulativeSeconds) >= 0
            ) {
                const totalSecondsFromDeparture = Number(backendCumulativeSeconds) + cumulativeStaySeconds;
                estimatedArrivalTime = new Date(
                    baseDateTime.getTime() + totalSecondsFromDeparture * 1000
                ).toISOString();
                hasEtaData = true;
            } else {
                cumulativeMinutes = cumulativeMinutes === null || tripDuration === null
                    ? null : cumulativeMinutes + tripDuration;

                if (hasValidBaseDateTime && cumulativeMinutes > 0) {
                    estimatedArrivalTime = new Date(baseDateTime.getTime() + cumulativeMinutes * 60000).toISOString();
                    hasEtaData = true;
                }
            }
        } else {
            if (!isSkipped) {
                cumulativeMinutes = cumulativeMinutes === null || tripDuration === null
                    ? null : cumulativeMinutes + tripDuration;
            }
            estimatedArrivalTime = isSkipped || !hasValidBaseDateTime || cumulativeMinutes === null
                ? null
                : new Date(baseDateTime.getTime() + cumulativeMinutes * 60000).toISOString();
            hasEtaData = !isSkipped && hasValidBaseDateTime && cumulativeMinutes !== null;
        }

        if (
            canUseStaticOperationalDelay
            && !isSkipped
            && hasValidBaseDateTime
            && wp.boundary_time_with_operational_delay
        ) {
            estimatedArrivalTime = wp.boundary_time_with_operational_delay;
            hasEtaData = true;
        }

        if (stayDuration > 0 && hasActivity && !isSkipped) {
            stayMinutes = stayDuration;
            if (usesStaticScheduleClock) {
                cumulativeStaySeconds += stayMinutes * 60;
            } else if (cumulativeMinutes !== null) {
                cumulativeMinutes += stayMinutes;
            }
        }

        mergedById.set(waypointId, {
            waypoint_id: waypointId,
            waypoint_name: wp.waypoint_name || stopData.waypoint_name,
            sequence_number: sequenceNumber,
            effective_sequence: effectiveSequence,
            point_type: pointType,
            estimated_arrival_time: estimatedArrivalTime,
            raw_eta_minutes_from_now: wp.eta_minutes_from_now ?? null,
            eta_minutes_with_operational_delay: canUseStaticOperationalDelay
                ? (wp.eta_minutes_with_operational_delay ?? null)
                : null,
            boundary_time_with_operational_delay: canUseStaticOperationalDelay
                ? (wp.boundary_time_with_operational_delay ?? null)
                : null,
            cumulative_arrival_delay_minutes: canUseStaticOperationalDelay
                ? (wp.cumulative_arrival_delay_minutes ?? 0)
                : 0,
            cumulative_departure_delay_minutes: canUseStaticOperationalDelay
                ? (wp.cumulative_departure_delay_minutes ?? 0)
                : 0,
            has_eta_data: hasEtaData,
            is_start_row: isStartRow,
            // Planned displays must preserve an unavailable static segment as
            // null. Treating it as zero would disagree with the static map
            // total and falsely imply that the leg has no distance.
            total_distance_meters: parseNullableNonNegativeMetric(wp[distanceField]),
            trip_duration_minutes: tripDuration,
            stay_duration_minutes: stayMinutes,
            can_pickup: stopData.can_pickup || false,
            can_dropoff: stopData.can_dropoff || false,
            pickup_riders: stopData.pickup_riders || 0,
            dropoff_riders: stopData.dropoff_riders || 0,
            pickup_luggages: stopData.pickup_luggages || 0,
            dropoff_luggages: stopData.dropoff_luggages || 0,
            is_user_pickup: stopData.is_user_pickup || waypointId === pickupStopId,
            is_user_dropoff: stopData.is_user_dropoff || waypointId === dropoffStopId,
            address: stopData.address,
            latitude: stopData.latitude,
            longitude: stopData.longitude,
            is_departure: isStartRow,
            boarding_location_info: stopData.boarding_location_info || wp.boarding_location_info || null,
            boarding_location_url: stopData.boarding_location_url || wp.boarding_location_url || null,
            boarding_location_image: stopData.boarding_location_image || wp.boarding_location_image || null,
            waypoint_arrival_time: stopData.waypoint_arrival_time ?? wp.waypoint_arrival_time ?? null,
            waypoint_departure_time: stopData.waypoint_departure_time ?? wp.waypoint_departure_time ?? null,
            is_passed: deriveDisplayPassedState(stopData, wp),
            is_skipped: isSkipped,
            is_manual_skip: Boolean(stopData.is_manual_skip),
            is_required: stopData.is_required,
            cumulative_distance_from_start_meters: stopData.cumulative_distance_from_start_meters ?? null,
            cumulative_duration_from_start_seconds: stopData.cumulative_duration_from_start_seconds ?? null,
        });
    }

    for (const stopData of tripStops || []) {
        const waypointId = Number(stopData?.waypoint_id);
        if (!Number.isFinite(waypointId) || mergedById.has(waypointId)) {
            continue;
        }

        const isHistorical = deriveDisplayPassedState(stopData) || deriveDisplaySkippedState(stopData);
        if (bypassedWaypointIds.has(waypointId) && !isHistorical) {
            continue;
        }

        if (stopData.point_type !== 'stop' && !isHistorical) {
            continue;
        }

        const isSkipped = deriveDisplaySkippedState(stopData);
        const backendCumulativeSeconds = stopData.cumulative_duration_from_start_seconds;
        const sequenceNumber = stopData.sequence_number ?? 0;
        const effectiveSequence = displaySequenceMap.get(waypointId);
        const pointType = stopData.point_type || 'stop';
        const isStartRow = isStartLikeWaypoint({
            ...stopData,
            sequence_number: sequenceNumber,
            effective_sequence: effectiveSequence,
            point_type: pointType
        });
        const hasEtaData = Boolean(
            hasValidBaseDateTime && !isSkipped && (
                isStartRow
                || (backendCumulativeSeconds != null && Number(backendCumulativeSeconds) >= 0)
            )
        );
        const estimatedArrivalTime = hasValidBaseDateTime && !isSkipped && isStartRow
            ? baseDateTime.toISOString()
            : (hasEtaData
                ? new Date(baseDateTime.getTime() + Number(backendCumulativeSeconds) * 1000).toISOString()
                : null);

        mergedById.set(waypointId, {
            waypoint_id: waypointId,
            waypoint_name: stopData.waypoint_name,
            sequence_number: sequenceNumber,
            effective_sequence: effectiveSequence,
            point_type: pointType,
            estimated_arrival_time: estimatedArrivalTime,
            has_eta_data: hasEtaData,
            is_start_row: isStartRow,
            total_distance_meters: null,
            trip_duration_minutes: null,
            stay_duration_minutes: getDisplayStayDurationMinutes({}, stopData, false),
            can_pickup: stopData.can_pickup || false,
            can_dropoff: stopData.can_dropoff || false,
            pickup_riders: stopData.pickup_riders || 0,
            dropoff_riders: stopData.dropoff_riders || 0,
            pickup_luggages: stopData.pickup_luggages || 0,
            dropoff_luggages: stopData.dropoff_luggages || 0,
            is_user_pickup: stopData.is_user_pickup || waypointId === pickupStopId,
            is_user_dropoff: stopData.is_user_dropoff || waypointId === dropoffStopId,
            address: stopData.address,
            latitude: stopData.latitude,
            longitude: stopData.longitude,
            is_departure: isStartRow,
            boarding_location_info: stopData.boarding_location_info || null,
            boarding_location_url: stopData.boarding_location_url || null,
            boarding_location_image: stopData.boarding_location_image || null,
            waypoint_arrival_time: stopData.waypoint_arrival_time ?? null,
            waypoint_departure_time: stopData.waypoint_departure_time ?? null,
            is_passed: deriveDisplayPassedState(stopData),
            is_skipped: isSkipped,
            is_manual_skip: Boolean(stopData.is_manual_skip),
            is_required: stopData.is_required,
            cumulative_distance_from_start_meters: stopData.cumulative_distance_from_start_meters ?? null,
            cumulative_duration_from_start_seconds: stopData.cumulative_duration_from_start_seconds ?? null,
        });
    }

    return sortWaypointsByEffectiveOrder(Array.from(mergedById.values()), displaySequenceMap);
};

/**
 * Calculate estimated waypoints for all statuses
 */
export const calculateETAWaypoints = (
    etaData,
    tripData,
    pickupStopId,
    dropoffStopId,
    tripPlan,
    entityTimeZone = null
) => {
    const tripStatus = tripData?.status;
    const isPreTrip = ['scheduled', 'dispatched'].includes(tripStatus);
    const tripStops = getTripDisplayStops(tripData);
    const liveStops = Array.isArray(tripData?.stops) ? tripData.stops : [];
    // Scheduled displays are a static route-segment projection. A retained
    // live/WebSocket payload may be stale from another lifecycle state, so it
    // is eligible only after the trip is actually in progress.
    const liveEtaPayload = tripStatus === 'in_progress'
        ? (etaData?.live || etaData?.websocket || null)
        : null;
    if ((!isPreTrip && !etaData?.estimated && !liveEtaPayload) || !tripStops.length) {
        console.warn('[ETADisplay] Missing data for waypoints calculation');
        return [];
    }

    // Get waypoints from either estimated or websocket data
    let waypoints = [];
    let isWebsocketData = false;

    if (liveEtaPayload) {
        waypoints = liveEtaPayload.waypoints || [];
        isWebsocketData = true;
        console.log('[ETADisplay] Using live ETA data for waypoint calculation');
    } else if (etaData?.estimated) {
        waypoints = getEstimatedWaypoints(etaData.estimated);
        console.log('[ETADisplay] Using estimated data for waypoint calculation');
    }

    const isPreTripFallback = isPreTrip && waypoints.length === 0;
    if (isPreTripFallback) {
        // Stops and the entity-anchored schedule survive a missing estimate.
        // Seed only structure: the normal static loop merges cumulative trip
        // metrics and preceding activity stays, without borrowing live or Delay
        // authority or making these rows look like successful request state.
        // A travel plan omits skipped interior stops. Use the interpolated
        // display rank for every seed so the later relevant-stop sort keeps
        // them between the fixed origin and destination. Mixing compressed
        // plan ranks with original trip ranks can move a skip past destination.
        const displaySequenceMap = tripPlan?.segments?.length
            ? buildDisplaySequenceMap(tripStops, tripPlan)
            : null;
        waypoints = tripStops.filter(stop => (
            stop.point_type === 'stop'
            || deriveDisplayPassedState(stop)
            || deriveDisplaySkippedState(stop)
        )).map(stop => ({
            waypoint_id: stop.waypoint_id,
            sequence_number: stop.sequence_number,
            // Without a plan, retain trip ranks consistently, including when
            // a partial stop list starts at a pickup rather than route origin.
            effective_sequence: displaySequenceMap?.get(Number(stop.waypoint_id))
                ?? stop.effective_sequence ?? stop.sequence_number,
            point_type: stop.point_type,
            total_distance_meters: null,
            total_duration_minutes: null
        }));
    }

    // For WebSocket data with in_progress trips, use the predicted_arrival_time directly
    if (isWebsocketData && tripStatus === 'in_progress') {
        return buildInProgressWebsocketWaypoints({
            waypoints,
            tripStops,
            liveStops,
            tripPlan,
            pickupStopId,
            dropoffStopId,
            retiredWaypointIds: liveEtaPayload?.eta_retirement?.waypoint_ids || []
        });
    }

    // Static estimates keep the persisted schedule as the raw anchor. Backend
    // per-waypoint effective arrivals may add operational Delay below; the
    // client never folds those minutes into route/stay duration.
    const baseDateTime = buildEntityScheduledDepartureDate(
        tripData.trip_date,
        tripData.departure_time,
        entityTimeZone
    );

    // Static Delay is backend-authored presentation authority. It may remain on
    // the already-selected historical fallback after Start, but never on a
    // live/WebSocket or ETA-disabled distance-only source. Keep this separate
    // from isPreTrip, which also owns clock construction and pre-trip labels.
    const canUseStaticOperationalDelay = [
        'scheduled',
        'dispatched',
        'in_progress'
    ].includes(tripStatus)
        && !isWebsocketData
        && !isPreTripFallback
        && etaData?.estimatedEtaDisabled !== true
        && !isDistanceOnlyEtaPayload(etaData?.estimated);
    return buildNonRealtimeDisplayWaypoints({
        waypoints,
        tripStops,
        tripPlan,
        pickupStopId,
        dropoffStopId,
        isPreTrip,
        canUseStaticOperationalDelay,
        baseDateTime,
        isWebsocketData
    });
};

/**
 * Process completed trip waypoints with actual vs estimated comparison
 */
const processCompletedWaypoints = (
    etaData,
    tripData,
    pickupStopId,
    dropoffStopId,
    tripPlan = null,
    entityTimeZone = null
) => {
    const estimatedWaypoints = Array.isArray(etaData?.estimated)
        ? etaData.estimated
        : (etaData?.estimated?.waypoints || []);
    // Completed comparison must project onto the effective/display stop set first;
    // otherwise raw actual rows can reintroduce bypassed waypoints like Bayview 401.
    const projectedRows = buildCompletedComparisonDisplayRows({
        actualWaypoints: etaData?.actual || [],
        estimatedWaypoints,
        websocketWaypoints: etaData?.websocket?.waypoints || [],
        tripData,
        tripPlan,
        pickupStopId,
        dropoffStopId
    });

    const estimatedArrivalMap = new Map();
    const baseDateTime = buildEntityScheduledDepartureDate(
        tripData.trip_date,
        tripData.departure_time,
        entityTimeZone
    );
    // Use the validated schedule anchor as the authority check rather than the
    // zone string's truthiness. This keeps comparisons unavailable even if a
    // malformed non-empty zone reaches this lower-level helper directly.
    const hasEntityTimeZoneAuthority = Boolean(
        baseDateTime && !isNaN(baseDateTime.getTime())
    );

    if (hasEntityTimeZoneAuthority) {
        let cumulativeEstimatedMinutes = 0;
        projectedRows.forEach((row) => {
            const estimatedStop = row.estimated_source || {};
            if (!estimatedStop?.waypoint_id) {
                estimatedArrivalMap.set(row.waypoint_id, null);
                return;
            }

            const tripDuration = parseFloat(estimatedStop.total_duration_minutes) || 0;
            const stayDuration = hasWaypointActivity(estimatedStop)
                ? parseFloat(estimatedStop.stay_duration_minutes) || 0
                : 0;
            cumulativeEstimatedMinutes += tripDuration + stayDuration;
            estimatedArrivalMap.set(
                row.waypoint_id,
                new Date(baseDateTime.getTime() + cumulativeEstimatedMinutes * 60000).toISOString()
            );
        });
    }

    return projectedRows.map((row) => {
        const actualStop = row.actual_source || {};
        const websocketStop = row.websocket_source || {};
        const estimatedArrivalTime = estimatedArrivalMap.get(row.waypoint_id) || null;
        let variance_minutes = null;

        if (hasEntityTimeZoneAuthority && estimatedArrivalTime && actualStop.actual_arrival_time) {
            variance_minutes = calculateInstantVarianceMinutes(
                actualStop.actual_arrival_time,
                estimatedArrivalTime
            );
        }

        let api_estimated_arrival_time = null;
        if (row.sequence_number === 0 || row.is_departure) {
            const startEstimate = getCompletedTripStartApiEstimate(etaData.websocket?.waypoints || []);
            if (startEstimate) {
                api_estimated_arrival_time = startEstimate.apiEstimatedArrivalTime;
                console.log('[ETADisplay] Calculated start time from first waypoint:', {
                    firstWaypoint: startEstimate.firstWaypoint.waypoint_name,
                    predictedArrival: startEstimate.firstWaypoint.predicted_arrival_time,
                    predictedTripDuration: startEstimate.firstWaypoint.predicted_trip_duration_minutes,
                    calculatedStart: api_estimated_arrival_time
                });
            }
        } else {
            api_estimated_arrival_time = websocketStop?.predicted_arrival_time || null;
        }

        let api_variance_minutes = null;
        if (hasEntityTimeZoneAuthority && api_estimated_arrival_time && actualStop.actual_arrival_time) {
            api_variance_minutes = calculateInstantVarianceMinutes(
                actualStop.actual_arrival_time,
                api_estimated_arrival_time
            );
        }

        return {
            waypoint_id: row.waypoint_id,
            waypoint_name: row.waypoint_name,
            sequence_number: row.sequence_number,
            effective_sequence: row.effective_sequence,
            point_type: row.point_type,
            can_pickup: row.can_pickup,
            can_dropoff: row.can_dropoff,
            address: row.address,
            latitude: row.latitude,
            longitude: row.longitude,

            // Use ACTUAL data for distances, durations, and stay times
            total_distance_meters: parseFloat(actualStop.total_distance_meters) || 0,
            total_duration_minutes: parseFloat(actualStop.total_duration_minutes) || 0,
            stay_duration_minutes: Math.round((Number(actualStop.stay_duration_seconds) || 0) / 60 * 10) / 10 || 0,
            distance_source: actualStop.distance_source ?? null,
            distance_authority: actualStop.distance_authority ?? null,
            is_completed_distance_fallback: Boolean(actualStop.is_completed_distance_fallback),

            // Arrival times
            actual_arrival_time: actualStop.actual_arrival_time || null,
            estimated_arrival_time: estimatedArrivalTime,
            api_estimated_arrival_time,

            // Passenger data from stop data
            pickup_riders: row.pickup_riders || 0,
            dropoff_riders: row.dropoff_riders || 0,
            pickup_luggages: row.pickup_luggages || 0,
            dropoff_luggages: row.dropoff_luggages || 0,
            is_user_pickup: row.is_user_pickup,
            is_user_dropoff: row.is_user_dropoff,
            boarding_location_info: row.boarding_location_info,
            boarding_location_url: row.boarding_location_url,
            boarding_location_image: row.boarding_location_image,

            // Variance for comparison
            variance_minutes,
            api_variance_minutes,

            // Next waypoint info
            next_waypoint_id: actualStop.next_waypoint_id,
            next_waypoint_name: actualStop.next_waypoint_name,

            // Skipped waypoint support
            is_skipped: row.is_skipped,
            is_manual_skip: row.is_manual_skip,
        };
    });
};

/**
 * Custom hook to determine which data to display based on trip status and available data
 */
export const useETADisplayData = (
    etaData,
    tripData,
    gpsActive,
    isDriver,
    pickupStopId,
    dropoffStopId,
    tripPlan = null,
    entityTimeZone = null
) => {
    const displayData = useMemo(() => {
        const status = tripData?.status;
        const liveEtaPayload = status === 'in_progress'
            ? (etaData?.live || etaData?.websocket || null)
            : null;
        const liveOperationalDelayDeclarations = Array.isArray(
            liveEtaPayload?.operational_delay_declarations
        )
            ? liveEtaPayload.operational_delay_declarations
            : [];
        const liveOperationalDelayProjectionWaypointOrderVersion =
            liveEtaPayload?.operational_delay_projection_waypoint_order_version;
        const estimatedOperationalDelayDeclarations = Array.isArray(
            etaData?.estimatedOperationalDelayDeclarations
        )
            ? etaData.estimatedOperationalDelayDeclarations
            : [];
        const estimatedOperationalDelayProjectionWaypointOrderVersion =
            etaData?.estimatedOperationalDelayProjectionWaypointOrderVersion;
        // Declarations without static rows cannot certify a fallback projection.
        const hasEstimatedRows = getEstimatedWaypoints(etaData?.estimated).length > 0;

        if (status === 'in_progress' && !liveEtaPayload) {
            // The phone GPS switch says nothing about an external device's ETA
            // authority. Retained route estimates cannot describe remaining
            // travel after Start. Keep topology, activity and actual passage,
            // without running the static schedule/Delay calculation at all.
            const tripStops = getTripDisplayStops(tripData);
            const waypoints = buildInProgressWebsocketWaypoints({
                // The builder applies bypass visibility after resolving live
                // corrections, so stale effective passage cannot restore a row.
                waypoints: tripStops,
                preserveExplicitPassageClears: true,
                tripStops,
                liveStops: tripData?.stops,
                tripPlan,
                pickupStopId,
                dropoffStopId
            }).map(waypoint => ({
                ...waypoint,
                estimated_arrival_time: null,
                dynamic_arrival_time: null,
                predicted_arrival_time: null,
                eta_minutes_from_now: null,
                raw_eta_minutes_from_now: null,
                eta_minutes_with_operational_delay: null,
                boundary_time_with_operational_delay: null,
                cumulative_arrival_delay_minutes: null,
                cumulative_departure_delay_minutes: null,
                total_distance_meters: null,
                distance_meters_from_current: null,
                cumulative_distance_from_start_meters: null,
                trip_duration_minutes: null,
                cumulative_duration_from_start_seconds: null,
                distance_source: null,
                segment_distance_source: null,
                distance_authority: null,
                eta_calculation_source: null,
                has_eta_data: false
            }));
            return {
                type: 'active_unavailable',
                source: 'unavailable',
                waypoints,
                duration_summary: null,
                isLive: false,
                operationalDelayDeclarations: []
            };
        }

        console.log(`[ETADisplay] Processing data for status: ${status}`);
        console.log(`[ETADisplay] WebSocket ETA available: ${!!etaData?.websocket}`);
        console.log(`[ETADisplay] GPS enabled: ${gpsActive}`);

        // Calculate estimated waypoints for all statuses
        const calculatedWaypoints = calculateETAWaypoints(
            etaData,
            tripData,
            pickupStopId,
            dropoffStopId,
            tripPlan,
            entityTimeZone
        );
        console.log(`[ETADisplay] Estimated waypoints:`, calculatedWaypoints);

        if (status === 'completed') {
            console.log('[ETADisplay] Trip completed - showing actual vs estimated data');

            if (etaData?.actual && etaData.actual.length > 0) {
                // Use actual data as the primary source for completed trips
                const processedWaypoints = processCompletedWaypoints(
                    etaData,
                    tripData,
                    pickupStopId,
                    dropoffStopId,
                    tripPlan,
                    entityTimeZone
                );
                const hasActualData = processedWaypoints.length > 0;

                return {
                    type: hasActualData ? 'completed_comparison' : 'completed_estimated_only',
                    waypoints: sortWaypointsByEffectiveOrder(processedWaypoints),
                    source: 'actual_trip_data'
                };
            }

            // Fallback to estimated data if no actual data available
            if (etaData?.estimatedEtaDisabled !== true && calculatedWaypoints.length > 0) {
                return {
                    type: 'completed_estimated_only',
                    waypoints: calculatedWaypoints,
                    source: 'calculated_from_trip_data'
                };
            }

            return {
                type: 'completed_no_data',
                waypoints: [],
                source: 'no_completed_data'
            };
        }

        if (status === 'canceled') {
            console.log('[ETADisplay] Trip canceled - no ETA data');
            return {
                type: 'canceled',
                waypoints: [],
                source: 'none'
            };
        }

        const isPreTripDistanceOnly = ['scheduled', 'dispatched'].includes(status)
            && etaData?.estimatedEtaDisabled === true;
        if (isPreTripDistanceOnly && calculatedWaypoints.length > 0) {
            // ETA may be disabled per entity, but planned static distances still
            // come from route_segments and must remain visible with the map.
            return {
                type: 'distance_only',
                waypoints: calculatedWaypoints.map(sanitizeDistanceOnlyDisplayWaypoint),
                duration_summary: null,
                source: 'none',
                isLive: false,
                etaDisabled: true
            };
        }

        if (status === 'in_progress' && liveEtaPayload) {
            console.log('[ETADisplay] Using WebSocket ETA data from etaData');

            if (isDistanceOnlyEtaPayload(liveEtaPayload)) {
                return {
                    type: 'distance_only',
                    waypoints: calculatedWaypoints.map(sanitizeDistanceOnlyDisplayWaypoint),
                    duration_summary: null,
                    source: 'none',
                    isLive: true,
                    etaDisabled: true
                };
            }

            return {
                type: 'websocket_realtime',
                waypoints: calculatedWaypoints,
                duration_summary: liveEtaPayload.duration_summary,
                source: liveEtaPayload.provider || 'websocket',
                isLive: true,
                operationalDelayDeclarations: liveOperationalDelayDeclarations,
                operationalDelayProjectionWaypointOrderVersion:
                    liveOperationalDelayProjectionWaypointOrderVersion
            };
        }

        if (['scheduled', 'dispatched'].includes(status) && calculatedWaypoints.length > 0) {
            console.log(`[ETADisplay] Trip ${status} - using calculated estimated data`);
            return {
                type: 'estimated',
                waypoints: calculatedWaypoints,
                source: 'route_estimates',
                operationalDelayDeclarations: hasEstimatedRows ? estimatedOperationalDelayDeclarations : [],
                operationalDelayProjectionWaypointOrderVersion:
                    hasEstimatedRows ? estimatedOperationalDelayProjectionWaypointOrderVersion : undefined
            };
        }

        console.log('[ETADisplay] No valid data found');
        return {
            type: 'no_data',
            waypoints: [],
            source: 'none'
        };
    }, [etaData, tripData, gpsActive, pickupStopId, dropoffStopId, tripPlan, entityTimeZone]);

    // The frozen last-known presentation flag lives on the ETA request state and
    // must reach the row-level display state. Every branch above builds its
    // return object explicitly, so the flag has to be re-attached here or it is
    // dropped before `etaStopDisplayState` can suppress live-only semantics.
    return useMemo(() => (
        etaData?.isStalePresentation === true
            ? { ...displayData, isStalePresentation: true }
            : displayData
    ), [displayData, etaData?.isStalePresentation]);
};

export default useETADisplayData;
